import { format, parse } from 'date-fns';
import {
    formatInCopenhagen,
    getCETTimestamp,
    reinterpretAsCET,
    utcFormat,
} from 'util/dateFnsHelpers';

const isValidDate = (dateObject: Date): boolean => {
    return dateObject instanceof Date && !Number.isNaN(dateObject.getTime());
};

const getTwoLength = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`;
};

export const getDateAndTime24Hour = (dateString: string): string => {
    if (!dateString || dateString.trim().length === 0) return '';
    const dateObject = new Date(dateString);

    if (!isValidDate(dateObject)) return 'Error parsing date';

    const date = getTwoLength(dateObject.getDate());
    const month = getTwoLength(dateObject.getMonth() + 1);
    const year = dateObject.getFullYear();
    const hours = getTwoLength(dateObject.getHours());
    const minutes = getTwoLength(dateObject.getMinutes());

    return `${date}-${month}-${year} ${hours}:${minutes}`;
};

export const getDateAndTime12Hour = (dateString: string): string => {
    if (!dateString || dateString.trim().length === 0) return '';
    const dateObject = new Date(dateString);

    if (!isValidDate(dateObject)) return 'Error parsing date';

    const localMinuteOffset =
        new Date().getTimezoneOffset() - dateObject.getTimezoneOffset();

    let hours = dateObject.getHours() - localMinuteOffset / 60;
    const meridiem = hours > 11 ? 'PM' : 'AM';
    hours = hours % 12 !== 0 ? hours % 12 : 12;

    const date = getTwoLength(dateObject.getDate());
    const month = getTwoLength(dateObject.getMonth() + 1);
    const year = dateObject.getFullYear();
    const minutes = getTwoLength(dateObject.getMinutes());

    return `${date}-${month}-${year} at ${hours}:${minutes} ${meridiem}`;
};

export const getDate = (dateString: string): string => {
    if (!dateString || dateString.trim().length === 0) return '';
    const dateObject = new Date(dateString);

    if (!isValidDate(dateObject)) return 'Error parsing date';

    const date = getTwoLength(dateObject.getDate());
    const month = getTwoLength(dateObject.getMonth() + 1);
    const year = dateObject.getFullYear();

    return `${date}-${month}-${year}`;
};

export const getDBTimestamp = (dateString: string): string => {
    return utcFormat(dateString, 'DD-MM-YYYY HH:mm:ss');
};

export { getCETTimestamp, reinterpretAsCET };

export function getRelativeTime(isoTime: string): string {
    const date = new Date(isoTime);
    const now = new Date();

    const diffMs = date.getTime() - now.getTime();
    const diffAbs = Math.abs(diffMs);

    const sec = Math.floor(diffAbs / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    const week = Math.floor(day / 7);
    const month = Math.floor(day / 30);
    const year = Math.floor(day / 365);

    const isFuture = diffMs > 0;

    const suffix = isFuture ? 'in ' : '';
    const postfix = isFuture ? '' : ' ago';

    const plural = (v: number) => (v > 1 ? 's' : '');

    if (day === 0 && hr < 24 && !isFuture) return 'today';
    if (day === 1 && !isFuture) return 'yesterday';
    if (day === 1 && isFuture) return 'tomorrow';

    if (sec < 60) return isFuture ? `in ${sec}s` : `${sec}s ago`;
    if (min < 60) return `${suffix}${min} min${plural(min)}${postfix}`;
    if (hr < 24) return `${suffix}${hr} hour${plural(hr)}${postfix}`;
    if (day < 7) return `${suffix}${day} day${plural(day)}${postfix}`;
    if (week < 5) return `${suffix}${week} week${plural(week)}${postfix}`;
    if (month < 12) return `${suffix}${month} month${plural(month)}${postfix}`;

    return `${suffix}${year} year${plural(year)}${postfix}`;
}

export function hhmmToDate(hhmm?: string): Date | null {
    if (!hhmm) return null;
    return parse(hhmm, 'HH:mm', new Date());
}

export function dateToHHMM(d: Date | null): string | undefined {
    if (!d) return undefined;
    return format(d, 'HH:mm');
}

export const getDateAndTime24HourCET = (dateString: string): string => {
    if (!dateString || dateString.trim().length === 0) return '';
    return formatInCopenhagen(dateString, 'DD-MM-YYYY HH:mm');
};

export function parseTimeToMinutes(s?: string) {
    if (!s) return null;
    const [hh, mm] = s.split(':');
    const h = Number(hh);
    const m = Number(mm);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
}
